import React from "react";
import { useMutation } from "@tanstack/react-query";
import { Form, Input, Button, Alert, Typography, Row, Col } from "antd";
import { useForm } from "antd/es/form/Form";
import { SiteReadable as Site } from "../../services/client/types.gen";
import { patchSiteMutation } from "../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../util-components/Notifications/notifications";
import CountryCodeSelect from "../FormComponents/CountryCodeSelect";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Title, Paragraph } = Typography;

type FormValues = {
  name: string;
  coordinates: string;
  country: string;
};

type SiteDetailsFormProps = {
  site: Site;
  refetchSite: () => void;
};

export const SiteDetailsForm: React.FC<SiteDetailsFormProps> = ({
  site,
  refetchSite,
}) => {
  const [form] = useForm<FormValues>();
  const notify = useNotify();
  const isViewer = useIsViewer();

  const patchSite = useMutation({
    ...patchSiteMutation(),
  });

  const initialValues: FormValues = {
    name: site.name,
    coordinates: `${site.location.coordinates[0]},${site.location.coordinates[1]}`,
    country: site.country,
  };

  const onFinish = (values: FormValues) => {
    const [longitude, latitude] = values.coordinates.split(",").map(Number);

    patchSite.mutate(
      {
        path: { site_id: site.site_id },
        body: {
          name: values.name,
          country: values.country,
          // location.name is left as stored: the dashboard keeps a geocoded
          // address there and this form has no address field to replace it with.
          location: {
            name: site.location.name,
            coordinates: [longitude, latitude, 0],
          },
        },
      },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong while updating the site details.",
          });
        },
        onSuccess: () => {
          refetchSite();
          notify({
            type: "success",
            message: "Site details updated successfully!",
          });
        },
      },
    );
  };

  return (
    <div className="mb-8">
      <Title level={3}>Site Details</Title>
      <Paragraph type="secondary" className="mb-4">
        The name and location of this site. The site ID cannot be changed.
      </Paragraph>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        layout="vertical"
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
        disabled={isViewer}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="name"
              label="Site Name"
              rules={[{ required: true, message: "Please enter a site name!" }]}
            >
              <Input placeholder="Enter site name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="coordinates"
              label="Location (Longitude, Latitude)"
              rules={[
                {
                  required: true,
                  message: "Please enter the coordinates!",
                },
                {
                  pattern: /^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/,
                  message: "Please enter valid coordinates (e.g., 12.34,56.78)",
                },
              ]}
            >
              <Input placeholder="Enter longitude, latitude" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="country"
              label="Country (BE, FR,...)"
              rules={[{ required: true, message: "Please select a country!" }]}
            >
              <CountryCodeSelect
                name="country"
                onSelect={(value) => form.setFieldValue("country", value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <DisableForViewer>
          <Button
            type="primary"
            htmlType="submit"
            loading={patchSite.isPending}
          >
            Update Site Details
          </Button>
        </DisableForViewer>

        {patchSite.isError && (
          <Alert
            message="Error"
            description="Something went wrong while updating the site details."
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
    </div>
  );
};

export default SiteDetailsForm;
